/**
 * useTodayMeals Hook
 * Fetches and transforms recipes for the current week to display today's meals and week summary
 *
 * Pattern: Reuses Calendar's approach - fetch week data once, transform client-side
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { recipeApiService } from '../services/recipeApiService'
import { RecipeRecord } from '../types/recipe'
import { MealAssignment, MealType } from '../types/calendar'
import { formatDateKey, getWeekDates, getTodayLocal } from '../utils/calendarUtils'

// Meal type display order (breakfast → lunch → dinner → snacks → dessert)
const MEAL_TYPE_ORDER: Record<MealType, number> = {
  breakfast: 0,
  lunch: 1,
  dinner: 2,
  snacks: 3,
  dessert: 4
}

interface UseTodayMealsReturn {
  /**
   * Today's meals sorted by meal time
   */
  todayMeals: MealAssignment[]

  /**
   * Week summary with meal counts and types per day
   */
  weekSummary: Array<{ date: string; mealCount: number; mealTypes: MealType[] }>

  /**
   * Full recipe records for the week (for caching/lookups)
   */
  weekRecipes: RecipeRecord[]

  /**
   * Loading state
   */
  isLoading: boolean

  /**
   * Error state
   */
  isError: boolean

  /**
   * Error object
   */
  error: Error | null

  /**
   * Manually refetch data
   */
  refetch: () => void
}

/**
 * Helper: Get start of week (Sunday)
 */
function startOfWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day
  return new Date(d.setDate(diff))
}

/**
 * Helper: Get end of week (Saturday)
 */
function endOfWeek(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() + (6 - day)
  return new Date(d.setDate(diff))
}

/**
 * Hook: Fetch and transform week data for Today view
 *
 * @param userId - User ID (custom UUID)
 * @returns Today's meals, week summary, and full week recipes
 */
export function useTodayMeals(userId: string | null): UseTodayMealsReturn {
  // Calculate current week date range (use getTodayLocal for consistent timezone)
  const today = getTodayLocal()
  const todayStr = formatDateKey(today) // Define stable today string
  const weekStart = startOfWeek(today)
  const weekEnd = endOfWeek(today)

  // Fetch recipes for current week (7 days)
  const {
    data: weekRecipes,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery<RecipeRecord[], Error>({
    queryKey: ['recipes-week', userId, formatDateKey(weekStart)],
    queryFn: async () => {
      if (!userId) {
        throw new Error('User ID is required')
      }

      return recipeApiService.getRecipesByDateRange(
        userId,
        formatDateKey(weekStart),
        formatDateKey(weekEnd)
      )
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    refetchOnWindowFocus: true
  })

  // Transform: Extract today's meals
  const todayMeals = useMemo(() => {
    if (!weekRecipes) return []

    const mealsToday: MealAssignment[] = []

    weekRecipes.forEach(recipe => {
      if (recipe.assigned_dates?.includes(todayStr)) {
        mealsToday.push({
          id: `${recipe.id}_${todayStr}`,
          recipeId: recipe.id,
          recipeName: recipe.recipe_data.name,
          mealType: recipe.recipe_data.mealType,
          date: todayStr,
          createdAt: recipe.created_at,
          updatedAt: recipe.updated_at
        })
      }
    })

    // Sort by meal time (breakfast → lunch → dinner → snacks → dessert)
    return mealsToday.sort((a, b) =>
      MEAL_TYPE_ORDER[a.mealType] - MEAL_TYPE_ORDER[b.mealType]
    )
  }, [weekRecipes, todayStr])

  // Transform: Calculate week summary (meal counts and types per day)
  const weekSummary = useMemo(() => {
    if (!weekRecipes) return []

    // Count meals and collect meal types per day
    const summary = new Map<string, { count: number; types: MealType[] }>()

    weekRecipes.forEach(recipe => {
      const mealType = recipe.recipe_data.mealType as MealType
      recipe.assigned_dates?.forEach(dateStr => {
        const existing = summary.get(dateStr) || { count: 0, types: [] }
        summary.set(dateStr, {
          count: existing.count + 1,
          types: [...existing.types, mealType]
        })
      })
    })

    // Convert to array format for WeeklyPreview component
    const weekDates = getWeekDates(0) // Current week
    return weekDates.map(date => {
      const dateKey = formatDateKey(date)
      const dayData = summary.get(dateKey) || { count: 0, types: [] }
      return {
        date: dateKey,
        mealCount: dayData.count,
        mealTypes: dayData.types
      }
    })
  }, [weekRecipes])

  return {
    todayMeals,
    weekSummary,
    weekRecipes: weekRecipes || [],
    isLoading,
    isError,
    error: error || null,
    refetch
  }
}

/**
 * Export helper functions for reuse
 * Note: getWeekDates is already exported from utils/calendarUtils.ts
 */
export { MEAL_TYPE_ORDER }